import type { Operators } from '@/shared/components/query-builder'
import type {
	AnchorGeneratedFamilyModel,
	AnchorGeneratedRecordModel,
	FilterRuleModel,
} from '@/shared/generated'
import type { Dispatch, SetStateAction } from 'react'

import { createContext, use, useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'

import { useGeneratedAnchorFamiliesQuery } from '@/entities/anchor'

import { useLocationQuery } from '@/shared/hooks/useLocationQuery'
import { useUpdateEffect } from '@/shared/hooks/useUpdateEffect'
import { useMutationObservers } from '@/shared/lib/useMutationObservers'

import { getSortedFamilyItems } from '../lib/helpers'

type SortedFamilyItem = Omit<
	AnchorGeneratedFamilyModel,
	'anchor_generated_records'
> & {
	anchor_generated_records: AnchorGeneratedRecordModel[]
}

type ApprovalPageContextValue = {
	selected: string[]
	data: SortedFamilyItem[]
	total: number
	isDataFetching: boolean
	isMutationPending: boolean
	filters: FilterRuleModel[]
	setSelected: Dispatch<SetStateAction<string[]>>
	isError: boolean
}
type MutationSuccessVariables = {
	filters: {
		name: string
		operation: Operators.IS | Operators.IN
		value: string[]
	}[]
}

export const ApprovalPageContext = createContext<ApprovalPageContextValue>({
	isDataFetching: false,
	isMutationPending: false,
	data: [],
	selected: [],
	total: 0,
	filters: [],
	setSelected: () => {},
	isError: false,
})

export const ApprovalPageProvider = ({
	children,
}: {
	children: React.ReactNode
}) => {
	const [selected, setSelected] = useState<string[]>([])
	const [{ search, limit, offset, filters }, setQuery] = useLocationQuery()

	const [searchTextDebounce] = useDebounceValue(search, 1000)

	const [isFamiliesReviewMutationPending, setIsFamiliesReviewMutationPending] =
		useState<boolean>(false)

	/**
	 * Mutation observer handlers
	 */
	const resetSelected = () => !!selected.length && setSelected([])
	const removeSelected = (ids: string[] | string) => {
		if (Array.isArray(ids)) {
			return setSelected((prev) => prev.filter((prev) => !ids.includes(prev)))
		}

		return setSelected((prev) => prev.filter((prev) => prev !== ids))
	}
	/**
	 * Handles selection state after successful mutation of anchor families.
	 * - If filter name is 'status': All families matching a status filter (e.g., 'review') were mutated,
	 *   so we reset the entire selection since all visible items changed status.
	 * - Otherwise: Only specific families (by IDs) were mutated,
	 *   so we remove only those IDs from the selection list.
	 */
	const onMutationSuccess = ({ filters }: MutationSuccessVariables) => {
		const filter = filters[0]
		filter?.name === 'status' ? resetSelected() : removeSelected(filter.value)
	}
	/**
	 * Mutation Observer
	 */
	useMutationObservers({
		mutationKey: ['anchor-generated-families-review'],
		onPending: () => setIsFamiliesReviewMutationPending(true),
		onSuccess: (variables) => {
			onMutationSuccess(variables as unknown as MutationSuccessVariables)
			setIsFamiliesReviewMutationPending(false)
		},
		onError: () => setIsFamiliesReviewMutationPending(false),
	})

	useUpdateEffect(() => {
		setQuery({ offset: 0 })
	}, [filters, searchTextDebounce])

	const requestBody = {
		limit,
		offset,
		filters,
		search: searchTextDebounce || '',
	}
	/**
	 * Queries
	 */
	const {
		data,
		isFetching: isDataFetching,
		isError,
	} = useGeneratedAnchorFamiliesQuery(requestBody)

	const value = {
		selected,
		data: getSortedFamilyItems(data?.data || []),
		total: data?.total || 0,
		isDataFetching,
		isMutationPending: isFamiliesReviewMutationPending,
		isError,
		filters,
		setSelected,
	}

	return <ApprovalPageContext value={value}>{children}</ApprovalPageContext>
}

export const useApprovalPageContext = () => use(ApprovalPageContext)
